import { ethers } from 'ethers';
import TrustlinedEscrowABI from './TrustlinedEscrow.json';

export interface Deposit {
  id: number; // Actual deposit ID from the contract
  depositor: string;
  beneficiary: string;
  amount: string;
  token: string;
  state: number;
  depositTimestamp: string;
  completionTimestamp: string;
}

export enum DepositState {
  AwaitingDeposit = 0,
  Deposited = 1,
  Released = 2,
  Refunded = 3
}

export const getContractByAddress = (provider: ethers.Provider, contractAddress: string) => {
  return new ethers.Contract(contractAddress, TrustlinedEscrowABI, provider);
};

export const getSignerContractByAddress = (signer: ethers.Signer, contractAddress: string) => {
  return new ethers.Contract(contractAddress, TrustlinedEscrowABI, signer);
};

export const validateEscrowContract = async (provider: ethers.Provider, contractAddress: string): Promise<boolean> => {
  try {
    const contract = getContractByAddress(provider, contractAddress);
    // Try to call the arbiter() function to verify it's a valid TrustlinedEscrow contract
    const arbiter = await contract.arbiter();
    // Check that arbiter is not zero address (which would indicate an invalid contract)
    return arbiter !== ethers.ZeroAddress;
  } catch (error) {
    console.warn('Contract validation failed:', error);
    return false;
  }
};

export const formatAmount = (amount: string, decimals: number = 18): string => {
  return ethers.formatUnits(amount, decimals);
};

export const parseAmount = (amount: string, decimals: number = 18): string => {
  return ethers.parseUnits(amount, decimals).toString();
};

export const getStateString = (state: number): string => {
  switch (state) {
    case DepositState.AwaitingDeposit:
      return 'Awaiting Deposit';
    case DepositState.Deposited:
      return 'Pending';
    case DepositState.Released:
      return 'Completed';
    case DepositState.Refunded:
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};

// ERC20 ABI for token symbol and decimals
const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)'
];

// Cache for token symbols to avoid repeated calls
const tokenSymbolCache = new Map<string, string>();

export const getTokenSymbol = async (provider: ethers.Provider, tokenAddress: string): Promise<string> => {
  // Return ETH for zero address
  if (tokenAddress === ethers.ZeroAddress) {
    return 'ETH';
  }

  // Check cache first
  if (tokenSymbolCache.has(tokenAddress)) {
    return tokenSymbolCache.get(tokenAddress)!;
  }

  try {
    // Create ERC20 contract instance
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    
    // Fetch symbol
    const symbol = await tokenContract.symbol();
    
    // Cache the result
    tokenSymbolCache.set(tokenAddress, symbol);
    
    return symbol;
  } catch (error) {
    console.warn(`Failed to fetch symbol for token ${tokenAddress}:`, error);
    // Return generic fallback
    return 'Token';
  }
};

export const formatTimestamp = (timestamp: string): string => {
  if (timestamp === '0') {
    return 'Not completed yet';
  }
  
  const date = new Date(Number(timestamp) * 1000);
  return date.toLocaleString();
};
